<template>
  <PremiumModalForm
    v-model="addMemberDialog"
    title="Add Member"
    submit-text="Add Member"
    :loading="savingMember"
    :max-width="600"
    @submit="submitAddMember"
  >
    <v-form ref="addMemberForm" v-model="validMemberForm" lazy-validation>
      <v-row>
        <!-- Unit Type (mixed societies only) -->
        <v-col v-if="isMixedSociety" cols="12" sm="6">
          <span class="pmf-lbl">Unit Type <span class="pmf-req">*</span></span>
          <div class="toggle-btn-group mt-2">
            <button
              type="button"
              class="toggle-btn"
              :class="{ active: newMember.unitType === '1' }"
              @click="newMember.unitType = '1'; newMember.houseShopNo = ''"
            >
              <v-icon small class="mr-1">mdi-home-outline</v-icon>
              House
            </button>
            <button
              type="button"
              class="toggle-btn"
              :class="{ active: newMember.unitType === '2' }"
              @click="newMember.unitType = '2'; newMember.houseShopNo = ''"
            >
              <v-icon small class="mr-1">mdi-store-outline</v-icon>
              Shop
            </button>
          </div>
        </v-col>

        <!-- Member Type -->
        <v-col cols="12" :sm="isMixedSociety ? 6 : 6">
          <span class="pmf-lbl">Member Type <span class="pmf-req">*</span></span>
          <div class="toggle-btn-group mt-2">
            <button
              type="button"
              class="toggle-btn"
              :class="{ active: newMember.memberType === 1 }"
              @click="newMember.memberType = 1"
            >
              <v-icon small class="mr-1">mdi-account-outline</v-icon>
              Owner
            </button>
            <button
              type="button"
              class="toggle-btn"
              :class="{ active: newMember.memberType === 2 }"
              @click="newMember.memberType = 2"
            >
              <v-icon small class="mr-1">mdi-account-key-outline</v-icon>
              Tenant
            </button>
          </div>
        </v-col>

        <!-- Block / Flat -->
        <v-col v-if="newMember.unitType !== '2'" cols="12" sm="6">
          <span class="pmf-lbl">
            Block 
            <span v-if="isBlockRequired" class="pmf-req">*</span>
          </span>
          <v-select
            v-model="newMember.flatId"
            :items="societyFlats"
            item-text="name"
            item-value="id"
            :placeholder="isBlockRequired ? 'Select Block' : 'Select Block (Optional)'"
            outlined
            dense
            hide-details="auto"
            class="pmf-input mt-1"
            :clearable="!isBlockRequired"
            :rules="isBlockRequired ? [v => !!v || 'Block is required'] : []"
          ></v-select>
        </v-col>

        <!-- House No -->
        <v-col cols="12" sm="6">
          <span class="pmf-lbl">
            {{ newMember.unitType === '2' ? 'Shop No' : 'House No' }} <span class="pmf-req">*</span>
          </span>
          <v-combobox
            v-model="newMember.houseShopNo"
            :items="availableUnits"
            item-text="label"
            item-value="value"
            :return-object="true"
            :placeholder="newMember.unitType === '2' ? 'Select or enter shop no' : 'Select or enter house no'"
            outlined
            dense
            hide-details="auto"
            :rules="[v => !!v || (newMember.unitType === '2' ? 'Shop No is required' : 'House No is required')]"
            class="pmf-input mt-1"
            :loading="unitsLoading"
          ></v-combobox>
        </v-col>

        <!-- Name -->
        <v-col cols="12" sm="12">
          <PremiumInput
            v-model="newMember.name"
            label="Name *"
            placeholder="Enter Member Name"
            icon="mdi-account-outline"
            :rules="[v => !!v || 'Name is required']"
          />
        </v-col>

        <!-- Mobile No -->
        <v-col cols="12" sm="12">
          <span class="pmf-lbl">Mobile Number <span class="pmf-req">*</span></span>
          <div class="d-flex align-start mt-1" style="gap: 8px;">
            <div class="pmf-country-wrap">
              <vue-country-code
                @onSelect="onSelectCountry"
                class="pmf-country-code"
                :defaultCountry="newMember.countryIso"
              />
            </div>
            <div class="flex-grow-1">
              <PremiumInput
                v-model="newMember.mobileNo"
                label=""
                placeholder="Enter Mobile Number"
                icon="mdi-phone-outline"
                type="number"
                :rules="[v => !!v || 'Mobile Number is required', v => (v && String(v).length >= 10 && String(v).length <= 10) || 'Must be 10 digits']"
              />
            </div>
          </div>
        </v-col>

        <!-- Email -->
        <v-col cols="12">
          <PremiumInput
            v-model="newMember.email"
            label="Email Address"
            placeholder="Enter Email Address"
            icon="mdi-email-outline"
            type="email"
          />
        </v-col>

 

        <!-- Access Flags -->
        <v-col cols="12">
          <div class="pmf-access-box">
            <v-checkbox
              v-model="newMember.grant_access"
              label="Grant Member Access (Auto-approve membership)"
              color="var(--accent)"
              hide-details
              class="ma-0 pa-0"
              dense
            ></v-checkbox>
            <v-checkbox
              v-model="newMember.request_approval"
              label="Request Approval Members (Grant approval access scopes)"
              color="var(--accent)"
              hide-details
              class="ma-0 pa-0 mt-2"
              dense
            ></v-checkbox>
          </div>
        </v-col>
      </v-row>
    </v-form>
  </PremiumModalForm>
</template>

<script>
import { mapMutations } from "vuex";
import PremiumModalForm from "../../common/PremiumModalForm.vue";
import PremiumInput from "../../common/PremiumInput.vue";

export default {
  name: "AddMemberModal",
  components: { PremiumModalForm, PremiumInput },
  props: {
    society_id: {
      type: String,
      required: true
    },
    society: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    societyType() {
      return Number(this.society.type || 1);
    },
    isBlockRequired() {
      // Required when: unit type is House (1) AND society has multiple buildings
      const isHouse = this.newMember.unitType === '1';
      const hasMultiple = this.society.has_multiple_building === 1 ||
        this.society.has_multiple_building === "1" ||
        this.society.has_multiple_building === true;
      return isHouse && hasMultiple;
    },
    isMixedSociety() {
      return this.societyType === 3;
    },
    isCommercialSociety() {
      return this.societyType === 2;
    },
  },
  watch: {
    'newMember.flatId'() {
      this.fetchAvailableUnits();
    },
    'newMember.unitType'() {
      this.newMember.houseShopNo = '';
      this.fetchAvailableUnits();
      // Reset validation so error messages update for the new unit type
      this.$nextTick(() => {
        if (this.$refs.addMemberForm) {
          this.$refs.addMemberForm.resetValidation();
        }
      });
    },
    'newMember.memberType'() {
      this.fetchAvailableUnits();
    }
  },
  data() {
    return {
      addMemberDialog: false,
      validMemberForm: true,
      savingMember: false,
      savingBlock: false,
      societyFlats: [],
      availableUnits: [],
      unitsLoading: false,
      newMember: {
      unitType: '1',
        memberType: 1,
        flatId: null,
        houseShopNo: '',
        name: '',
        mobileNo: '',
        email: '',
        grant_access: true,
        request_approval: true,
        countryCode: '+91',
        countryIso: 'in'
      },
    };
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),

    openModal() {
      // Set unitType based on society type:
      // 1 = Residential (House), 2 = Commercial (Shop), 3 = Mixed (default House)
      const defaultUnitType = this.isCommercialSociety ? '2' : '1';
      this.newMember = {
        unitType: defaultUnitType,
        memberType: 1,
        flatId: null,
        houseShopNo: '',
        name: '',
        mobileNo: '',
        email: '',
        grant_access: true,
        request_approval: true,
        countryCode: '+91',
        countryIso: 'in'
      };
      if (this.$refs.addMemberForm) {
        this.$refs.addMemberForm.resetValidation();
      }
      this.fetchSocietyFlats();
      this.fetchAvailableUnits();
      this.addMemberDialog = true;
    },

    onSelectCountry({ iso2, dialCode }) {
      this.newMember.countryCode = "+" + dialCode;
      this.newMember.countryIso = iso2.toLowerCase();
    },

    fetchAvailableUnits() {
      if (!this.society_id) return;
      this.unitsLoading = true;
      const params = {};
      if (this.newMember.memberType) params.member_type = this.newMember.memberType;
      if (this.newMember.unitType) params.unit_type = this.newMember.unitType;
      if (this.newMember.flatId) params.flat_id = this.newMember.flatId;

      this.$axios.get(`superadmin/society/${this.society_id}/units/available`, { params })
        .then(response => {
          this.unitsLoading = false;
          if (response.data.status) {
            this.availableUnits = response.data.data.map(u => ({
              label: u.block_name ? `${u.block_name}-${u.house_number}` : u.house_number,
              value: String(u.house_number)
            }));
          }
        })
        .catch(() => { this.unitsLoading = false; });
    },

    fetchSocietyFlats(selectNewId = null) {
      if (!this.society_id) return;
      this.$axios.get(`society/${this.society_id}/flats`).then(res => {
        if (res.data.status) {
          this.societyFlats = res.data.flats || [];
          if (selectNewId) {
            this.newMember.flatId = selectNewId;
          }
        }
      });
    },



    submitAddMember() {
      if (!this.$refs.addMemberForm.validate()) return;
      this.savingMember = true;

      let finalHouseShopNo = this.newMember.houseShopNo;
      if (typeof finalHouseShopNo === 'object' && finalHouseShopNo !== null) {
        finalHouseShopNo = finalHouseShopNo.value;
      }

      const payload = { ...this.newMember, houseShopNo: finalHouseShopNo };

      this.$axios
        .post(`superadmin/society/${this.society_id}/members`, payload)
        .then(res => {
          this.savingMember = false;
          if (res.data.status) {
            this.setAlert({ showAlert: true, variant: "green accent-4", timeout: 3000, message: "Member added successfully" });
            this.addMemberDialog = false;
            this.$emit("update-society");
          } else {
            const msg = (res.data.errors && res.data.errors.length)
              ? res.data.errors[0].message
              : (res.data.message || "Failed to add member");
            this.setAlert({ showAlert: true, variant: "red accent-3", timeout: 3000, message: msg });
          }
        })
        .catch(() => {
          this.savingMember = false;
          this.setAlert({ showAlert: true, variant: "red accent-3", timeout: 3000, message: "Something went wrong" });
        });
    },
  }
};
</script>

<style scoped>
.pmf-lbl {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  text-transform: uppercase;
  display: block;
}
.pmf-req { color: #ef4444; }

.pmf-link {
  font-size: 11.5px;
  font-weight: 600;
  color: var(--accent);
  text-decoration: none;
  cursor: pointer;
}
.pmf-link:hover { text-decoration: underline; }

.pmf-save-btn {
  height: 40px;
  padding: 0 14px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  flex-shrink: 0;
}
.pmf-save-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.pmf-cancel-btn {
  height: 40px;
  width: 40px;
  background: var(--border-color2, rgba(0,0,0,0.06));
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pmf-cancel-btn:hover { background: rgba(0,0,0,0.1); }

.pmf-access-box {
  background: rgba(242, 101, 34, 0.04);
  border: 1px solid rgba(242, 101, 34, 0.15);
  border-radius: 10px;
  padding: 12px 16px;
}

.pmf-input :deep(.v-input__slot) {
  background: var(--bg-primary, #f8fafc) !important;
  border-radius: 8px !important;
}

/* Remove PremiumInput default bottom margin inside modal */
::v-deep .premium-input-wrapper {
  margin-bottom: 4px !important;
}

/* Toggle button group (Unit Type / Member Type) */
.toggle-btn-group {
  display: flex;
  gap: 8px;
}

.toggle-btn {
  flex: 1;
  padding: 9px 12px;
  border: 2px solid var(--border-color2, rgba(0,0,0,0.1));
  border-radius: 10px;
  background: transparent;
  color: var(--text-secondary, #64748b);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-btn:hover {
  border-color: var(--accent);
  color: var(--accent);
  background: rgba(242, 101, 34, 0.06);
}

.toggle-btn.active {
  background: rgba(242, 101, 34, 0.08);
  border-color: var(--accent);
  border-width: 2px;
  color: var(--accent);
  font-weight: 700;
}

/* Country Code Select */
.pmf-country-wrap {
  width: 88px;
  flex-shrink: 0;
  display: flex;
  align-self: flex-start;
}
.pmf-country-code {
  width: 100% !important;
  height: 40px !important;
  border-radius: 8px !important;
  border: 1px solid var(--border-color2, rgba(0,0,0,0.1)) !important;
  background: var(--bg-primary, #f8fafc) !important;
  font-family: "Inter", sans-serif;
  box-shadow: none !important;
  padding: 0 !important;
}
.pmf-country-code :deep(.selected-flag) {
  width: 100% !important;
  height: 38px !important;
  padding: 0 8px !important;
  border-radius: 8px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: transparent !important;
  outline: none !important;
}
.pmf-country-code :deep(.selected-flag:focus),
.pmf-country-code :deep(.selected-flag:hover) {
  background: rgba(0,0,0,0.03) !important;
}
.pmf-country-code :deep(.vti__flag) {
  margin: 0 2px 0 0 !important;
}
.pmf-country-code :deep(.vti__dropdown-arrow) {
  font-size: 10px;
  margin-left: 2px;
}
.pmf-country-code :deep(.dropdown) {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  font-family: "Inter", sans-serif;
  font-size: 13px;
  border: 1px solid var(--border-color2, rgba(0,0,0,0.1));
}
.pmf-country-code :deep(.dropdown-item) {
  font-size: 13px;
  padding: 8px 12px;
}
.pmf-country-code :deep(.dropdown-item:hover) {
  background-color: var(--bg-primary, #f1f5f9);
}
</style>
